import { GPU_METRICS_CONFIG } from './constants';
import type { PrometheusMetric } from './types';

/**
 * Prometheus 메트릭에서 labels 추출
 * @param metric - Prometheus 메트릭 객체
 * @returns labels 객체
 */
export function extractLabels(metric: PrometheusMetric): Record<string, string> {
  const labels: Record<string, string> = {};
  Object.entries(metric).forEach(([key, value]) => {
    if (value != null) {
      labels[key] = String(value);
    }
  });
  return labels;
}

/**
 * GPU 인스턴스 displayName 생성
 * @param hostname - Worker hostname
 * @param gpu - GPU 번호
 * @param gpuInstanceId - GPU 인스턴스 ID
 * @param namespace - Kubernetes namespace (선택)
 * @returns 포맷된 displayName
 * @example
 * createGpuDisplayName('worker007', '0', '1', 'sangmin-env')
 * // Returns: "worker007-GPU0-I1 (sangmin-env)"
 */
export function createGpuDisplayName(hostname: string, gpu: string, gpuInstanceId: string, namespace?: string): string {
  const baseName = `${hostname}-GPU${gpu}-I${gpuInstanceId}`;
  return namespace ? `${baseName} (${namespace})` : baseName;
}

/**
 * Prometheus 쿼리용 시간 범위 파라미터 생성
 * @returns start, end, step 타임스탬프
 */
export function createTimeRangeParams() {
  const now = Math.floor(Date.now() / 1000);
  return {
    start: now - GPU_METRICS_CONFIG.RANGE_SECONDS,
    end: now,
    step: GPU_METRICS_CONFIG.STEP_SECONDS,
  };
}

/**
 * 시계열 데이터에서 통계 계산
 * @param values - 값 배열
 * @returns 평균, 최신값, 최댓값
 * @note 빈 배열의 경우 모든 값이 0으로 반환됩니다
 */
export function calculateStats(values: number[]) {
  if (values.length === 0) {
    return { mean: 0, last: 0, max: 0 };
  }

  const mean = values.reduce((sum, v) => sum + v, 0) / values.length;
  const last = values[values.length - 1] || 0;
  const max = Math.max(...values);

  return { mean, last, max };
}

/**
 * Prometheus 메트릭에서 GPU 기본 정보 추출
 * @param metric - Prometheus 메트릭 객체
 * @returns GPU 메타데이터 (hostname, gpu, device, displayName 등)
 * @example
 * const base = extractGpuMetricBase(item.metric);
 * // { hostname: 'worker007', gpu: '0', gpuInstanceId: '1', namespace: 'dev-env', ... }
 */
export function extractGpuMetricBase(metric: PrometheusMetric) {
  const hostname = metric.Hostname || metric.hostname || 'unknown';
  const gpu = metric.gpu || '0';
  const gpuInstanceId = metric.GPU_I_ID || '';
  const namespace = metric.namespace || '';
  const device = metric.device || `nvidia${gpu}`;

  const labels = extractLabels(metric);
  const displayName = createGpuDisplayName(hostname, gpu, gpuInstanceId, namespace);

  return {
    hostname,
    gpu,
    gpuInstanceId,
    namespace,
    device,
    displayName,
    labels,
  };
}
